/**
 * Sprint RBAC-X Slice 1.5 — AccessRestricted role explainer helpers.
 *
 * Maps backend role keys to user-friendly labels and produces a
 * plain-English reason for the AccessRestricted page so users see
 * WHY they were blocked without exposing route internals, DB ids,
 * permission keys or stack traces.
 */

export const ROLE_LABELS = {
  super_admin:        "Diplomexa Platform Admin",
  org_admin:          "Organisation Admin",
  event_manager:      "Event Manager",
  school_admin:       "School Admin",
  faculty_advisor:    "Faculty Advisor",
  delegate:           "Delegate",
  chair:              "Committee Chair",
  volunteer:          "Volunteer",
  parent:             "Parent",
  sponsor:            "Sponsor",
  judge:              "Judge",
  partner_institution:"Partner Institution",
  secretariat:        "Secretariat",
};

export function roleLabel(roleKey) {
  if (!roleKey) return "Guest";
  return ROLE_LABELS[roleKey] || roleKey.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Resolve the user-friendly area name for a blocked route.
 * Kept VERY small and conservative — we deliberately give vague
 * labels (e.g. "Platform settings") instead of leaking internal
 * sensitive feature names.
 */
const AREA_LABELS = [
  // [regex on pathname, friendly area label]
  [/\/dashboard\/organizations/,                        "Platform Organisations"],
  [/\/dashboard\/admin\/diploks\/reconciliation/,       "Diploks Reconciliation"],
  [/\/dashboard\/admin\/diploks\/adjustments/,          "System Credit Adjustments"],
  [/\/dashboard\/admin\/diploks\/vouchers-promos/,      "Diploks Vouchers & Promos"],
  [/\/dashboard\/admin\/diploks\/feature-costs/,        "Premium Feature Costs"],
  [/\/dashboard\/admin\/diploks/,                       "Diploks Administration"],
  [/\/dashboard\/admin\/goodwill-audit/,                "Goodwill Credit Audit"],
  [/\/dashboard\/admin\/access-control/,                "Access Control"],
  [/\/dashboard\/watchdog/,                             "System Health Monitor"],
  [/\/dashboard\/gateway-review/,                       "Payment Gateway Review"],
  [/\/dashboard\/revenue/,                              "Revenue Dashboard"],
  [/\/dashboard\/rbac\/advanced/,                       "Role & Permission Management"],
  [/\/dashboard\/audit/,                                "Audit Logs"],
  [/\/dashboard\/lms\/admin\/live-provider-settings/,   "Live Classroom Platform Settings"],
  [/\/dashboard\/diploks\/wallet/,                      "Organisation Diploks Wallet"],
  [/\/dashboard\/access\/scanner/,                      "AccessPass Scanner"],
  [/\/dashboard\/admin\/email-templates/,               "Email Template Library"],
  [/\/dashboard\/diploks\/organiser\/grants/,           "Organiser Diploks Grants"],
];

export function areaLabelForPath(pathname) {
  if (!pathname) return "this area";
  for (const [pattern, label] of AREA_LABELS) {
    if (pattern.test(pathname)) return label;
  }
  return "this area";
}

/**
 * Compose a friendly explanation sentence — never reveals exact
 * permission keys, DB ids, or backend route names.
 */
export function buildReason({ userRole, allowedRoles }) {
  const userLabel = roleLabel(userRole);
  if (!allowedRoles || allowedRoles.length === 0) {
    return `Your current role (${userLabel}) does not have access to this area.`;
  }
  const allowedLabels = allowedRoles.map(roleLabel);
  // Dedupe + cap to 3 for visual clarity.
  const uniq = Array.from(new Set(allowedLabels)).slice(0, 3);
  const tail = allowedLabels.length > 3 ? ", and others" : "";
  if (uniq.length === 1) {
    return `Your current role is ${userLabel}, but this area is reserved for ${uniq[0]}.`;
  }
  return `Your current role is ${userLabel}, but this area is reserved for ${uniq.slice(0, -1).join(", ")} and ${uniq[uniq.length - 1]}${tail}.`;
}
